import type { BrewerModel } from './brewers.types';
import type { BaseModel } from './common.types';
import type { TechnicalSupportProviderModel } from './technical-support-providers.types';
import type { UserModel } from './users.types';

export enum ContainerType {
  BIB = 'BIB',
  CASK = 'Cask',
  KEG = 'Keg',
  N_A = 'N/A'
}

export enum CouplerType {
  A_TYPE = 'A-Type',
  BIB = 'BIB',
  D_TYPE = 'D-Type',
  G_TYPE = 'G-Type',
  G_TYPE_S_TYPE = 'G-Type & S-Type',
  S_TYPE = 'S-Type',
  U_TYPE = 'U-Type',
  N_A = 'N/A'
}

export enum NozzleType {
  CREAMER = 'Creamer',
  FREE_FLOW = 'Free Flow',
  SPARKLER = 'Sparkler',
  N_A = 'N/A'
}

export enum StyleType {
  BITTER = 'Bitter',
  STOUT = 'Stout',
  ALE = 'Ale',
  PALE_ALE_TRADITIONAL = 'Pale Ale Traditional',
  PALE_ALE_CRAFT = 'Pale Ale Craft',
  IPA_TRADITIONAL = 'IPA Traditional',
  IPA_CRAFT = 'IPA Craft',
  MILD = 'Mild',
  SPECIALTY = 'Specialty',
  WHEAT_BEER = 'Wheat Beer',
  BELGIAN_BEER = 'Belgian Beer',
  SOUR_BEER = 'Sour Beer',
  LAGER_DOMESTIC_MM = 'Lager Domestic MM',
  LAGER_NON_DOMESTIC_MM = 'Lager Non-domestic MM',
  LAGER_CRAFT = 'Lager Craft',
  LAGER_PILSNER = 'Lager Pilsner',
  CIDER = 'Cider',
  PERRY = 'Perry',
  WINE = 'Wine',
  COCKTAIL = 'Cocktail',
  SEASONAL = 'Seasonal'
}

export enum GasType {
  CO2 = 'CO2',
  MIX_30_70 = '30/70',
  MIX_60_40 = '60/40',
  N_A = 'N/A'
}

export enum StatusType {
  IN_PRODUCTION = 'In production',
  DELISTED = 'Delisted',
  ARCHIVED = 'Archived',
  UNCONFIRMED = 'Unconfirmed'
}

export enum TapSealType {
  FULL_POUR = 'Full Pour',
  CREAMER = 'Creamer',
  NONE = 'None'
}

export enum AlcoholByVolumeBandType {
  ALCOHOL_FREE = 'Alcohol Free',
  LOW_ALCOHOL = 'Low Alcohol',
  ALCOHOLIC = 'Alcoholic'
}

export enum GlassType {
  BRANDED = 'Branded',
  GENERIC = 'Generic'
}

export interface ProductModel extends BaseModel {
  // Product Data
  name: string;
  status: StatusType;
  // Style Data
  style?: StyleType;
  alcoholByVolume?: number;
  alcoholByVolumeBand?: AlcoholByVolumeBandType;
  glutenFree?: boolean;
  vegan?: boolean;
  // Cellar Setup Data
  containerType?: ContainerType;
  couplerType?: CouplerType;
  gas1?: GasType;
  gas2?: GasType;
  co2vol?: number;
  pumpRecommended?: boolean;
  secondaryCoolingRecommended?: boolean;
  // Bar Setup Data
  nozzleType?: NozzleType;
  tapSealType?: TapSealType;
  nozzleHolesCount?: number;
  nozzleHolesDiameter?: number;
  glassType?: GlassType;
  glassNucleated?: boolean;
  // Dispense Specs Data
  dispenseSpeedMin?: number;
  dispenseSpeedMax?: number;
  dispenseTempMin?: number;
  dispenseTempMax?: number;
  headMin?: number;
  headMax?: number;
  settlingTime?: number;
  // Relations
  brewerId?: string;
  technicalSupportProviderId?: string;
  brewer?: BrewerModel;
  technicalSupportProvider?: TechnicalSupportProviderModel;
  suggestedBy?: UserModel;
}

export type CreateProductParams = Omit<
  ProductModel,
  'id' | 'brewer' | 'technicalSupportProvider' | 'createdAt' | 'updatedAt' | 'technicalSupportProviderId'
>;

export interface UpdateProductParams extends CreateProductParams {}

export interface UpdateProductParamsWithId extends UpdateProductParams {
  productId: string;
}

export interface DeleteProductParams {
  productId: string;
}

export interface ProductsFiltersProps {
  brewerName?: string;
  style?: StyleType;
  containerType?: ContainerType;
  nozzleType?: NozzleType;
  tapSealType?: TapSealType;
  couplerType?: CouplerType;
  alcoholByVolumeBand?: AlcoholByVolumeBandType;
  vegan?: boolean;
  glutenFree?: boolean;
  status?: StatusType;
  name?: string;
}

export type ListProductModel = Pick<
  ProductModel,
  'id' | 'name' | 'style' | 'containerType' | 'nozzleType' | 'couplerType' | 'status'
> & {
  brewerName?: string;
};

export type ProductSortingFields =
  | 'name'
  | 'style'
  | 'containerType'
  | 'nozzleType'
  | 'couplerType'
  | 'status'
  | 'brewerName';

export interface ProductBulkItem {
  name: string;
  brewerId: string;
  style?: StyleType;
  glassType?: GlassType;
  glassNucleated?: boolean;
  secondaryCoolingRecommended?: boolean;
  pumpRecommended?: boolean;
  containerType?: ContainerType;
  couplerType?: CouplerType;
  nozzleType?: NozzleType;
  gas1?: GasType;
  gas2?: GasType;
  alcoholByVolumeBand?: AlcoholByVolumeBandType;
  tapSealType?: TapSealType;
  dispenseSpeedMin?: number;
  dispenseSpeedMax?: number;
  dispenseTempMin?: number;
  dispenseTempMax?: number;
  alcoholByVolume?: number;
  co2vol?: number;
  headMin?: number;
  headMax?: number;
  settlingTime?: number;
  nozzleHolesCount?: number;
  nozzleHolesDiameter?: number;
  vegan?: boolean;
  glutenFree?: boolean;
}

export interface BulkProductParams {
  createProducts: ProductBulkItem[];
}

export interface BulkProductValidationRow {
  name: string;
  brewer: { id: string; name: string | null } | null;
  style: string;
  glassType: string;
  glassNucleated: string;
  secondaryCoolingRecommended: string;
  pumpRecommended: string;
  containerType: string;
  couplerType: string;
  nozzleType: string;
  gas1: string;
  gas2: string;
  alcoholByVolumeBand: string;
  tapSealType: string;
  dispenseSpeedMin: string;
  dispenseSpeedMax: string;
  dispenseTempMin: string;
  dispenseTempMax: string;
  alcoholByVolume: string;
  co2vol: string;
  headMin: string;
  headMax: string;
  settlingTime: string;
  nozzleHolesCount: string;
  nozzleHolesDiameter: string;
  vegan: string;
  glutenFree: string;
  existingProduct: { id: string; name: string; brewerId: string | null; status: string } | null;
  errors: string[];
  warnings: string[];
}
